import { supabase } from '../lib/supabaseClient'
import type {
  PaymentError,
  PlanType,
  VerifyTransactionRequest,
  VerifyTransactionResponse,
} from '../types/payment'

/**
 * Payment Service
 * Centralized service for handling all payment-related API calls
 * Uses Supabase session tokens for authentication
 */

/**
 * Create payment via Supabase Edge Function
 * This calls the server-side create-payment function which initializes Paystack
 */
/**
 * The only plan keys the create-payment Edge Function accepts. Anything else
 * is a bug on the caller's side, so fail loudly before hitting the network.
 */
const VALID_PLAN_TYPES: readonly PlanType[] = [
  'single_scan',
  'weekly',
  'monthly',
  'yearly',
]

export const createPayment = async (
  planType: PlanType,
  options?: { scanId?: string },
): Promise<{ authorization_url: string }> => {
  try {
    if (!VALID_PLAN_TYPES.includes(planType)) {
      throw new Error(
        `Invalid plan type "${planType}". Expected one of: ${VALID_PLAN_TYPES.join(', ')}`,
      )
    }
    // Get current session for access token
    const {
      data: { session },
      error: sessionError,
    } = await supabase.auth.getSession()

    console.log('Session check:', {
      hasSession: !!session,
      sessionError: sessionError?.message,
      userId: session?.user?.id,
      email: session?.user?.email,
      tokenLength: session?.access_token?.length,
    })

    if (sessionError) {
      console.error('Session error:', sessionError)
      throw new Error(`Session error: ${sessionError.message}`)
    }

    if (!session) {
      console.error('No active session found')
      throw new Error('No active session. Please sign in again.')
    }

    const { user } = session

    if (!user.email) {
      throw new Error('User email not found')
    }

    const requestBody: Record<string, unknown> = {
      email: user.email,
      user_id: user.id,
      plan_type: planType,
    }

    // Single-report unlock: $2.99 (299 cents, USD) tied to this exact scan.
    // The webhook reads metadata to set scans.is_unlocked server-side.
    // No is_subscription flag for single scans.
    if (options?.scanId) {
      requestBody.scan_id = options.scanId
      if (planType === 'single_scan') {
        requestBody.amount = 299
        requestBody.currency = 'USD'
        requestBody.metadata = { user_id: user.id, scan_id: options.scanId }
        requestBody.callback_url = `${window.location.origin}/payment-callback?scan_id=${encodeURIComponent(options.scanId)}`
      }
    } else {
      // A plan purchase isn't tied to a report — drop any stale report
      // hand-off so the callback doesn't route back to an old scan.
      try {
        localStorage.removeItem('td_pending_unlock_scan')
      } catch {
        // ignore
      }
    }

    console.log('Creating payment with:', {
      planType,
      email: user.email,
      userId: user.id,
      scanId: options?.scanId,
      requestBody,
    })

    // Call Supabase Edge Function
    const response = await fetch(
      'https://neadfoxscgjeekvabnoz.supabase.co/functions/v1/create-payment',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(requestBody),
      },
    )

    console.log('Payment API response status:', response.status)
    console.log('Payment API response headers:', {
      contentType: response.headers.get('content-type'),
      contentLength: response.headers.get('content-length'),
    })

    // Get response text first to handle both JSON and non-JSON responses
    const responseText = await response.text()
    console.log('Payment API raw response:', responseText)

    if (!response.ok) {
      let errorMessage = 'Payment initialization failed'
      try {
        const errorData = JSON.parse(responseText)
        console.error('Payment API error data:', errorData)
        errorMessage = errorData.error || errorData.message || errorMessage
      } catch (e) {
        console.error('Could not parse error response:', responseText)
        errorMessage = `Payment failed (${response.status}): ${responseText.substring(0, 100)}`
      }
      throw new Error(errorMessage)
    }

    // Parse the response
    let data: any
    try {
      data = JSON.parse(responseText)
      console.log('Payment API parsed response:', data)
      console.log('Response structure:', {
        hasAuthorizationUrl: 'authorization_url' in data,
        hasData: 'data' in data,
        hasAuthorizationUrlInData:
          data.data && 'authorization_url' in data.data,
        keys: Object.keys(data),
      })
    } catch (e) {
      console.error('Could not parse success response:', responseText)
      throw new Error('Invalid response from payment service')
    }

    // Handle different possible response structures
    let authorizationUrl: string | undefined

    // Check direct property
    if (data.authorization_url) {
      authorizationUrl = data.authorization_url
      console.log('Found authorization_url at root level')
    }
    // Check nested data property (common Supabase pattern)
    else if (data.data && data.data.authorization_url) {
      authorizationUrl = data.data.authorization_url
      console.log('Found authorization_url in data.data')
    }
    // Check for Paystack direct response
    else if (data.authorizationUrl) {
      authorizationUrl = data.authorizationUrl
      console.log('Found authorizationUrl (camelCase)')
    }
    // Check for url property
    else if (data.url) {
      authorizationUrl = data.url
      console.log('Found url property')
    }

    console.log('Extracted authorization URL:', authorizationUrl)

    if (!authorizationUrl) {
      console.error('No authorization URL found in response:', {
        fullResponse: data,
        responseKeys: Object.keys(data),
        responseType: typeof data,
      })
      throw new Error(
        `No authorization URL in response. API returned: ${JSON.stringify(data).substring(0, 200)}`,
      )
    }

    return { authorization_url: authorizationUrl }
  } catch (error: any) {
    console.error('Create payment error:', {
      message: error.message,
      stack: error.stack,
      name: error.name,
    })
    throw error
  }
}

/**
 * Verify a transaction status
 * @param reference - Payment reference from Paystack
 * @returns Promise with transaction verification result
 */
export async function verifyTransaction(
  reference: string,
  context?: { scanId?: string | null; userId?: string | null },
): Promise<VerifyTransactionResponse> {
  // Get current session
  const {
    data: { session },
  } = await supabase.auth.getSession()

  if (!session) {
    throw new Error('No active session. Please log in.')
  }

  const requestBody: VerifyTransactionRequest = {
    reference,
  }
  if (context?.scanId) requestBody.scan_id = context.scanId
  if (context?.userId) requestBody.user_id = context.userId

  // Call verify-transaction edge function with session token
  const { data, error } = await supabase.functions.invoke(
    'verify-transaction',
    {
      body: requestBody,
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    },
  )

  if (error) {
    console.error('Transaction verification error:', error)
    throw new Error(error.message || 'Failed to verify transaction')
  }

  if (!data) {
    throw new Error('No response from verification service')
  }

  // Check for error in response data
  if ('error' in data) {
    const paymentError = data as PaymentError
    throw new Error(paymentError.message || paymentError.error)
  }

  return data as VerifyTransactionResponse
}

/**
 * Helper function to redirect to payment authorization URL
 * @param authorizationUrl - The URL to redirect to
 */
export function redirectToPayment(authorizationUrl: string): void {
  window.location.href = authorizationUrl
}
